import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

export const isPaid = (user) => ["plus", "premium"].includes(user?.plan_tier);

// Applies a `{plan, grocery}` response to the react-query cache so plan + grocery stay in sync
export function usePlanActions() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [busy, setBusy] = useState(null);

  const apply = (data) => {
    if (data?.plan) qc.setQueryData(["mealplan"], (old) => ({ ...(old || { exists: true }), exists: true, plan: data.plan }));
    if (data?.grocery) qc.setQueryData(["grocery"], { exists: true, ...data.grocery });
  };

  const run = async (key, fn, successMsg, { paid = false } = {}) => {
    if (paid && !isPaid(user)) {
      toast(t("premium_feature"), { action: { label: t("pricing"), onClick: () => nav("/pricing") } });
      return null;
    }
    setBusy(key);
    try {
      const { data } = await fn();
      apply(data);
      if (successMsg) toast.success(successMsg);
      return data;
    } catch (e) {
      if (e.response?.status === 402) toast(t("premium_feature"), { action: { label: t("pricing"), onClick: () => nav("/pricing") } });
      else toast.error(e.response?.data?.detail || "Error");
      return null;
    } finally { setBusy(null); }
  };

  return {
    busy, apply,
    optimize: () => run("optimize", () => api.post("/meal-plan/optimize"), t("plan_updated")),
    regenerateDay: (day) => run(`day-${day}`, () => api.post("/meal-plan/regenerate-day", { day }), t("day_regenerated"), { paid: true }),
    swap: (day, slot, recipe_id) => run(`swap-${day}-${slot}`, () => api.post("/meal-plan/swap", { day, slot, recipe_id }), t("plan_updated"), { paid: !recipe_id }),
    setPortions: (day, slot, member_id, portions) => run(`portions-${day}-${slot}`, () => api.post("/meal-plan/portions", { day, slot, member_id, portions }), t("plan_updated")),
    exclude: (ingredient) => run("exclude", () => api.post("/meal-plan/exclude-ingredient", { ingredient }), t("excluded_done"), { paid: true }),
    alternatives: async (day, slot) => {
      if (!isPaid(user)) { toast(t("premium_feature"), { action: { label: t("pricing"), onClick: () => nav("/pricing") } }); return null; }
      try { return (await api.get("/meal-plan/alternatives", { params: { day, slot } })).data.alternatives; }
      catch (e) { toast.error(e.response?.data?.detail || "Error"); return null; }
    },
  };
}
